import React, {useEffect, useState} from 'react';
import {Link} from 'react-router-dom';
import {Card} from 'baseui/card';
import {Accordion, Panel} from 'baseui/accordion';
import {HIERARCHY, Tag} from 'baseui/tag';
import {FaCheck, FaRegStar, FaStar} from 'react-icons/fa';
import QuestionTimestamp from '../Items/QuestionTimestamp';
import {Button, KIND, SHAPE, SIZE} from "baseui/button";
import {Heading, HeadingLevel} from "baseui/heading";
import {LabelSmall, LabelXSmall, ParagraphSmall} from "baseui/typography";
import {LuBookmark, LuExternalLink, LuEye, LuHeart, LuNotebookText} from "react-icons/lu";
import {CiMenuKebab} from "react-icons/ci";
import {IoCheckmarkCircleOutline, IoCheckmarkDoneCircle} from "react-icons/io5";
import FormattedrawCode from "../Items/FormattedCode";
import { hasBrainstormExtraContent } from "../../utils/extraInfoImages";
import {FlexGrid, FlexGridItem} from "baseui/flex-grid";
import {Block, BlockProps} from "baseui/block";
import {StatefulPopover} from "baseui/popover";
import ExceptionItem from "../Items/ExceptionItem";
import {Modal, ModalBody, ModalButton, ModalFooter, ModalHeader} from "baseui/modal";
import {FormControl} from "baseui/form-control";
import {Input} from "baseui/input";
import {Textarea} from "baseui/textarea";
import {MdOutlineDeleteSweep} from "react-icons/md";
import {TiEdit} from "react-icons/ti";
import {TbDatabaseExport} from "react-icons/tb";
import apiClient from "../shared/hooks/AuthService";
import {BsDatabaseCheck} from "react-icons/bs";
import QuestionExplainWrapper from "../QuestionExplainWrapper";
import { decodeHtmlEntities, formatStructuredAnswer } from "../../utils/formatStructuredAnswer";
import { renderInlineMarkdown } from "../../utils/renderInlineMarkdown";
import { useStyletron } from "baseui";


export default function QuestionCardItem({
                                             index,
                                             isAdmin,
                                             questionItem,
                                             editingIndex,
                                             toggleReadStatus,
                                             readStatus,
                                             readCount = 0,
                                             liked = false,
                                             handleSetReadStatus,
                                             handleDeleteClick,
                                             setStatusMessage,
                                             handleTextareaResize,
                                             setQuestionData,
                                             saved = false,
                                             onToggleSave,
                                             userId,
                                         }) {
    const [, theme] = useStyletron();
    const [detailsOpen, setDetailsOpen] = useState(true);
    const [editMode, setEditMode] = useState(false);

    const [localItem, setLocalItem] = useState({
        question: '',
        answer: '',
        extraInfo: '',
    });

    const itemProps: BlockProps = {
        backgroundColor: "mono300",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
    };

    const handleEditClick = (index) => {
        setEditMode(!editMode); // Toggle edit mode
    };

// Handle submit, sync localItem to parent state and save
    const submitHandler = (index) => {
        handleSaveEdit(index, localItem);
        setEditMode(false);
    };

// Only set the localItem when the modal is opened (editMode is true)
    useEffect(() => {
        if (editMode && questionItem) {
            setLocalItem({
                question: questionItem.question,
                answer: questionItem.answer,
                extraInfo: questionItem.extraInfo,
            });
        }
    }, [editMode, questionItem]);

    const handleSaveEdit = async (index, updatedItem) => {
        const updatedQuestionData = {
            questionId: index,
            question: updatedItem.question,
            answer: updatedItem.answer,
            extraInfo: updatedItem.extraInfo
        };

        try {
            const response = await apiClient.patch(`/api/update-question`, updatedQuestionData);

            if (response.status === 200) {
                setQuestionData((prevState) => {
                    const updatedData = [...prevState];
                    updatedData[index] = {
                        ...updatedData[index],
                        ...updatedItem
                    };
                    setStatusMessage("Question updated successfully");
                    return updatedData;
                });
            } else {
                throw new Error("Failed to save changes.");
            }
        } catch (err) {
            console.error("Error saving question:", err.message);
        }
    };

    const getDisplayCategory = (category, maxLength = 12) => {
        const lowerCaseCategory = category.toLowerCase();

        if (lowerCaseCategory.length > maxLength) {
            // Create acronym: first letter of each word, capitalized
            return lowerCaseCategory
                .split(' ')
                .map(word => word.charAt(0).toUpperCase())
                .join('');
        } else {
            return category; // or you could return category.charAt(0).toUpperCase() + category.slice(1)
        }
    };

    const extraInfoText = questionItem.extraInfo ?? '';
    const hasExtraInfoBody = hasBrainstormExtraContent(
        extraInfoText,
        questionItem.mediumUrl
    );
    const likeCount = Math.max(0, Number(questionItem.rating) || 0);

    // Avoid updating the parent state unless the user submits the edit
    return (
        <>
            <Card className="card-body question-card-root"
                  key={questionItem.id}
                  overrides={{
                      Root: {
                          style: {
                              width: '100%',
                              paddingLeft: '10px',
                              paddingRight: '10px',
                              marginBottom: '20px',
                              boxSizing: 'border-box',
                              display: 'flex',
                              flexDirection: 'column',
                              position: 'relative'
                          },
                      }
                  }}
            >
                <Tag
                   hierarchy={HIERARCHY.secondary}
                    size={"small"}
                    closeable={false}
                    overrides={{
                        Root: {
                            style: {
                                position: 'absolute',
                                top: '-10px',
                                left: '-10px',
                                zIndex: 0,
                                fontSize: '8px',
                                padding: '10px 16px 7px',
                                borderRadius: '9px',
                                backgroundColor: '#eff6ff',
                                color: '#1d4ed8',
                                border: '1px solid #bfdbfe',
                            },
                        },
                    }}
                >
                    <p>{decodeHtmlEntities(questionItem.subCategory2)}</p>
                </Tag>
                <Block marginBottom='-11px'>
                    <div className="accordion">
                        {/* Details accordion */}
                        <Accordion
                            className="accordionPanel"
                            expanded={true}
                            onChange={({expanded}) => setDetailsOpen(expanded.includes('details'))}
                            overrides={{
                                Root: {
                                    style: {
                                        width: 'auto',
                                        marginBottom: '0px',
                                        marginLeft: '-20px',
                                        marginRight: '-20px',
                                        marginTop: '0px',
                                    }
                                },
                                PanelContainer: {
                                    style: {
                                        // borderBottom: 'none', // Remove bottom border
                                        marginBottom: 'auto',  // Remove bottom margin
                                        width: '100%',
                                        borderBottomWidth: '0',
                                    },
                                }, Content: {
                                    style: {
                                        paddingBottom: '0px', // Remove bottom padding
                                        marginBottom: '0px',  // Remove bottom margin
                                    },
                                },
                            }}>

                            <Panel className="panel-1" key="details" style={{width: '100%'}} expanded={true} title={
                                <FlexGrid
                                    flexGridColumnCount={[1, 1, 2, 2]}
                                    flexGridColumnGap="scale400"
                                    flexGridRowGap="scale400"
                                    $style={{
                                        width: '-webkit-fill-available',
                                        paddingBottom: '0px',
                                    }}
                                >
                                    <FlexGridItem flexGridItemIndex={index} {...itemProps} style={{
                                        justifyContent: 'flex-start',
                                        backgroundColor: 'white',
                                        flexWrap: 'inherit'
                                    }}>
                                        <HeadingLevel>
                                            <Heading
                                                styleLevel={6}
                                                style={{
                                                    margin: 0,
                                                    lineHeight: '1.4',
                                                    textAlign: 'left',
                                                }}
                                            >
                                                {index + 1}. {decodeHtmlEntities(questionItem.question)}{' '}
                                            </Heading>
                                        </HeadingLevel>
                                    </FlexGridItem>

                                    <FlexGridItem {...itemProps}
                                                  style={{backgroundColor: 'white', justifyContent: 'flex-end'}}>
                                        <div
                                            className="question-card-tags"
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'flex-end',
                                                width: '100%',
                                                flexWrap: 'wrap',
                                                gap: '6px',
                                            }}
                                        >
                                            <Tag
                                                closeable={false}
                                                kind={KIND.black}
                                                hierarchy={HIERARCHY.secondary}
                                            >
                                                {{
                                                    basics: (
                                                        <>
                                                            <FaStar size={11}/>
                                                            <FaRegStar size={11}/>
                                                            <FaRegStar size={11}/>
                                                        </>
                                                    ),
                                                    intermediate: (
                                                        <>
                                                            <FaStar size={11}/>
                                                            <FaStar size={11}/>
                                                            <FaRegStar size={11}/>
                                                        </>
                                                    ),
                                                    advanced: (
                                                        <>
                                                            <FaStar size={11}/>
                                                            <FaStar size={11}/>
                                                            <FaStar size={11}/>
                                                        </>
                                                    ),
                                                    expert: (
                                                        <>
                                                            <FaStar size={11}/>
                                                            <FaStar size={11}/>
                                                            <FaStar size={11}/>
                                                        </>
                                                    ),
                                                    experts: (
                                                        <>
                                                            <FaStar size={11}/>
                                                            <FaStar size={11}/>
                                                            <FaStar size={11}/>
                                                        </>
                                                    ),
                                                }[questionItem.subCategory1?.toLowerCase?.()]}
                                            </Tag>
                                            <Link
                                                to={`/question/${questionItem.id}`}
                                                className="question-card-action-btn question-card-action-btn--open"
                                                onClick={(event) => event.stopPropagation()}
                                            >
                                                <LuExternalLink aria-hidden size={14} />
                                                Open
                                            </Link>
                                            <button
                                                type="button"
                                                className={[
                                                    'question-card-action-btn',
                                                    'question-card-action-btn--save',
                                                    saved ? 'question-card-action-btn--saved' : '',
                                                ]
                                                    .filter(Boolean)
                                                    .join(' ')}
                                                onClick={(event) => {
                                                    event.stopPropagation();
                                                    onToggleSave?.(questionItem.id);
                                                }}
                                                aria-label={saved ? 'Remove saved question' : 'Save question'}
                                            >
                                                <LuBookmark aria-hidden size={14} />
                                                {saved ? 'Saved' : 'Save'}
                                            </button>

                                            <Tag
                                                closeable={false}
                                                kind={KIND.black}
                                                hierarchy={HIERARCHY.secondary}
                                                overrides={{
                                                    Root: {
                                                        style: {
                                                            fontSize: '11px',
                                                            '@media screen and (min-width: 600px)': {
                                                                fontSize: '12px',
                                                            },
                                                            '@media screen and (min-width: 900px)': {
                                                                fontSize: '13px',
                                                            },
                                                        },
                                                    },
                                                    EndEnhancer: { style: { paddingLeft: '4px' } },
                                                    StartEnhancer: { style: { paddingRight: '4px' } },
                                                }}
                                            >
                                                <LuNotebookText size={13}/>{' '}
                                                {getDisplayCategory(questionItem.category)}
                                            </Tag>

                                            <Tag
                                                closeable={false}
                                                kind={KIND.black}
                                                hierarchy={HIERARCHY.secondary}
                                                overrides={{
                                                    Root: {
                                                        style: {
                                                            fontSize: '10px',
                                                            '@media screen and (min-width: 600px)': {
                                                                fontSize: '12px',
                                                            },
                                                            '@media screen and (min-width: 900px)': {
                                                                fontSize: '13px',
                                                            },
                                                        },
                                                    },
                                                    EndEnhancer: { style: { paddingLeft: '4px' } },
                                                    StartEnhancer: { style: { paddingRight: '4px' } },
                                                }}>
                                                <QuestionTimestamp
                                                    addedOn={questionItem.addedOn}
                                                    icon={<BsDatabaseCheck size={13}/>}
                                                />
                                            </Tag>
                                            {isAdmin ?
                                                <div>
                                                    {editingIndex === index ? (
                                                        <FaCheck onClick={() => handleSaveEdit(index)}/>
                                                    ) : (
                                                        <div>
                                                            <Button kind="tertiary" shape="round" size={SIZE.mini}
                                                                    onClick={() => handleEditClick(index)}>
                                                                <TiEdit size={19}/>
                                                            </Button>
                                                            <Button kind="tertiary" shape="round" size={SIZE.mini}
                                                                    onClick={() => handleDeleteClick(index)}>
                                                                <MdOutlineDeleteSweep size={20}/>
                                                            </Button>
                                                        </div>
                                                    )}
                                                </div> : null}
                                            <div>
                                                <StatefulPopover
                                                    content={() => (
                                                        <ExceptionItem userId={userId}
                                                                       questionId={questionItem.id}
                                                                       setStatusMessage={setStatusMessage}/>
                                                    )}>
                                                    <div style={{position: 'relative'}}>
                                                        <CiMenuKebab style={{cursor: 'pointer'}}/>
                                                    </div>
                                                </StatefulPopover>
                                            </div>
                                        </div>
                                    </FlexGridItem>
                                </FlexGrid>
                            }>
                                <ParagraphSmall className={readStatus[questionItem.id] ? "crossed" : ""} style={{
                                    marginTop: '-26px',
                                    marginBottom: '5px',
                                    fontSize: '15px',
                                    fontWeight: '300',
                                    lineHeight: 1.55,
                                    textAlign: 'left',
                                    whiteSpace: 'pre-wrap',
                                    color: '#334155',
                                }}>
                                    {renderInlineMarkdown(
                                        formatStructuredAnswer(questionItem.answer),
                                        theme
                                    )}
                                </ParagraphSmall>
                                <Accordion
                                            className="accordionPanel"
                                            expanded={detailsOpen ? ['details'] : []}
                                            onChange={({expanded}) => setDetailsOpen(expanded.includes('details'))}
                                            overrides={{
                                                Root: {
                                                    style: {
                                                        width: 'auto',
                                                        marginBottom: '-16px',
                                                        marginLeft: '-22px',
                                                        marginRight: '-20px',
                                                        marginTop: '-5px',
                                                    }
                                                }
                                            }}>
                                            <Panel
                                                key="details"
                                                title={
                                                    <div className="question-card-extra-row">
                                                        <Button kind={KIND.secondary} shape={SHAPE.pill}
                                                                size={SIZE.mini}>
                                                            <LabelXSmall>Extra Info</LabelXSmall>
                                                        </Button>
                                                        <Button
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                toggleReadStatus(questionItem.id);
                                                            }}
                                                            kind={KIND.tertiary}
                                                            shape={SHAPE.round}
                                                            size={SIZE.mini}
                                                            style={{padding: '2px 2px'}}>
                                                            {readStatus[questionItem.id] ?
                                                                <IoCheckmarkDoneCircle size={26}/> :
                                                                <IoCheckmarkCircleOutline size={26}/>
                                                            }
                                                        </Button>
                                                        <div className="question-card-metrics">
                                                            <span
                                                                className="question-card-metric question-card-metric--reads"
                                                                title="Times you marked this question as read"
                                                            >
                                                                <LuEye aria-hidden size={14} />
                                                                {readCount}{' '}
                                                                {readCount === 1 ? 'read' : 'reads'}
                                                            </span>
                                                            <span
                                                                className="question-card-metric question-card-metric--likes"
                                                                title="Community likes on this answer"
                                                            >
                                                                <LuHeart aria-hidden size={14} />
                                                                {likeCount}{' '}
                                                                {likeCount === 1 ? 'like' : 'likes'}
                                                            </span>
                                                            {saved ? (
                                                                <span
                                                                    className="question-card-metric question-card-metric--saved"
                                                                    title="In your saved deck"
                                                                >
                                                                    <LuBookmark aria-hidden size={14} />
                                                                    Saved
                                                                </span>
                                                            ) : null}
                                                            {liked ? (
                                                                <span
                                                                    className="question-card-metric question-card-metric--liked-you"
                                                                    title="You liked this answer"
                                                                >
                                                                    <LuHeart aria-hidden size={14} />
                                                                    You liked
                                                                </span>
                                                            ) : null}
                                                        </div>
                                                    </div>
                                                }
                                            >
                                                {hasExtraInfoBody ? (
                                                    <FormattedrawCode questionItem={questionItem}
                                                                          data={readStatus}
                                                                          setReadStatus={handleSetReadStatus}
                                                                          columnDefinition="BIGINT DEFAULT 0"
                                                                          setStatusMessage={setStatusMessage}/>
                                                    ) :
                                                    <LabelSmall $style={{fontWeight: 'lighter'}}>No extra information
                                                        present, kindly edit to provide the same.</LabelSmall>
                                                }
                                                <QuestionExplainWrapper question={questionItem} />
                                            </Panel>
                                        </Accordion>
                            </Panel>
                        </Accordion>
                    </div>
                </Block>
            </Card>
            <Modal
                onClose={() => setEditMode(false)}
                isOpen={editMode}
                overrides={{
                    Dialog: {
                        style: {
                            width: 'min(80vw, 720px)',
                            maxWidth: '96vw',
                            height: 'min(80vh, 720px)',
                            maxHeight: '92vh',
                            display: 'flex',
                            flexDirection: 'column',
                        },
                    },
                }}
            >
                <ModalHeader>Edit Question Item: {questionItem.id}</ModalHeader>
                <ModalBody style={{flex: "1 1 0"}}>
                    <FormControl label="Question">
                        <Input
                            id="question-input"
                            value={localItem.question}
                            onChange={(e) => setLocalItem(prev => ({...prev, question: e.target.value}))}
                        />
                    </FormControl>
                    <FormControl label="Answer">
                        <Input
                            id="answer-input"
                            value={localItem.answer}
                            onChange={(e) => setLocalItem(prev => ({...prev, answer: e.target.value}))}
                        />
                    </FormControl>
                    <FormControl label="Extra Information" style={{width: "100%"}}>
                        <Textarea
                            id="textarea-id"
                            value={localItem.extraInfo}
                            onInput={handleTextareaResize}
                            onChange={(e) => setLocalItem(prev => ({...prev, extraInfo: e.target.value}))}
                            clearable
                            resize="both"
                            style={{width: "100%"}}
                        />
                    </FormControl>
                </ModalBody>
                <ModalFooter>
                    <ModalButton kind="tertiary" onClick={() => setEditMode(false)}>
                        Cancel
                    </ModalButton>
                    <ModalButton onClick={() => submitHandler(index)}>
                        <TbDatabaseExport/>&nbsp;
                        Submit</ModalButton>
                </ModalFooter>
            </Modal>
        </>
    );
}
